/**
 * Statement page — accessibility statement (WCAG declaration).
 *
 * Shows the summary card, embeddable HTML preview, and coverage stats.
 * Clearly notes that automated testing alone cannot claim full compliance.
 *
 * Supports multi-language UI labels around the generated statement.
 * The actual statement HTML is still generated in English (Phase 1).
 */

import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import DOMPurify from "dompurify";
import {
  useLatestStatement,
  useStatementPayload,
} from "../hooks/use-statement.js";
import { WarningPanel } from "../components/warning-panel.js";
import { Breadcrumbs } from "../components/breadcrumbs.js";
import { PageSkeleton } from "../components/skeleton.js";
import {
  STATEMENT_LANGUAGES,
  STATEMENT_UI_LABELS,
  type StatementLanguageCode,
} from "../components/statement-languages.js";

export function StatementPage() {
  const { websiteId } = useParams<{ websiteId: string }>();
  const [lang, setLang] = useState<StatementLanguageCode>("en");

  const labels = STATEMENT_UI_LABELS[lang];

  const { data: summary, isLoading: summaryLoading, isError: summaryError } =
    useLatestStatement(websiteId);
  const { data: payload, isLoading: payloadLoading } = useStatementPayload(
    websiteId,
    summary?.id,
  );

  const loading = summaryLoading || (summary != null && payloadLoading);

  if (loading) return (
    <div className="product-page">
      <Breadcrumbs
        items={[
          { label: "Websites", to: "/websites" },
          { label: "Website", to: `/websites/${websiteId}` },
          { label: "Statement" },
        ]}
      />
      <PageSkeleton />
    </div>
  );

  if (!summary || !payload) {
    return (
      <div className="product-page">
        <Breadcrumbs
          items={[
            { label: "Websites", to: "/websites" },
            { label: "Website", to: `/websites/${websiteId}` },
            { label: "Statement" },
          ]}
        />
        <h1 className="page-title">{labels.title}</h1>
        <LanguageSelector lang={lang} onLangChange={setLang} />
        {summaryError ? (
          <div className="alert alert--error" role="alert">
            Failed to load statement data. Try refreshing the page.
          </div>
        ) : (
        <>
        {labels.languageNotice && (
          <div className="alert alert--info" role="status">
            {labels.languageNotice}
          </div>
        )}
        <p className="text-muted">{labels.noStatementYet}</p>
        <Link
          to={`/websites/${websiteId}/scan`}
          className="btn btn--primary mt-2"
        >
          {labels.runScan}
        </Link>
        </>
        )}
      </div>
    );
  }

  const statusLabel: Record<string, string> = {
    partially_compliant: "Partially compliant",
    non_compliant: "Non-compliant",
    not_assessed: "Not assessed",
  };

  return (
    <div className="product-page">
      <Breadcrumbs
        items={[
          { label: "Websites", to: "/websites" },
          { label: payload.websiteDomain ?? "Website", to: `/websites/${websiteId}` },
          { label: "Statement" },
        ]}
      />
      <h1 className="page-title">{labels.title}</h1>

      <LanguageSelector lang={lang} onLangChange={setLang} />

      {labels.languageNotice && (
        <div className="alert alert--info" role="status">
          {labels.languageNotice}
        </div>
      )}

      <p className="page-subtitle">
        {labels.generatedOn}{" "}
        {new Date(payload.generatedAt).toLocaleString(lang)} —{" "}
        {payload.websiteDomain}
      </p>

      {/* -- Summary card --------------------------------------------------- */}
      <section className="product-section" aria-labelledby="stmt-summary-h">
        <h2 id="stmt-summary-h" className="section-title">
          {labels.summary}
        </h2>
        <table className="review-table">
          <tbody>
            <tr>
              <td>{labels.wcagLevel}</td>
              <td>{payload.wcagLevel}</td>
            </tr>
            <tr>
              <td>{labels.complianceStatus}</td>
              <td>
                {statusLabel[payload.accessibilityStatus] ??
                  payload.accessibilityStatus}
              </td>
            </tr>
            <tr>
              <td>{labels.score}</td>
              <td>
                {payload.scoreValue != null
                  ? `${Math.round(payload.scoreValue)} / 100`
                  : "\u2014"}
              </td>
            </tr>
            <tr>
              <td>{labels.manualReview}</td>
              <td>{payload.manualReviewStatus.replace(/_/g, " ")}</td>
            </tr>
            {payload.orgName && (
              <tr>
                <td>{labels.organisation}</td>
                <td>{payload.orgName}</td>
              </tr>
            )}
          </tbody>
        </table>

        <p className="alert alert--info">
          {payload.automatedTestingLimitations}
        </p>
      </section>

      {/* -- Coverage ------------------------------------------------------- */}
      <section className="product-section" aria-labelledby="stmt-coverage-h">
        <h2 id="stmt-coverage-h" className="section-title">
          {labels.coverage}
        </h2>
        <div className="stat-grid">
          <div className="stat-item">
            <span className="stat-item__value">
              {payload.coverage.pagesScanned}
            </span>
            <span className="stat-item__label">{labels.pagesScanned}</span>
          </div>
          <div className="stat-item">
            <span className="stat-item__value">
              {payload.coverage.pagesDiscovered}
            </span>
            <span className="stat-item__label">{labels.pagesDiscovered}</span>
          </div>
          <div className="stat-item">
            <span className="stat-item__value">
              {payload.coverage.coveragePercent}%
            </span>
            <span className="stat-item__label">{labels.coveragePercent}</span>
          </div>
          <div className="stat-item">
            <span className="stat-item__value">
              {payload.coverage.templatesScanned}
            </span>
            <span className="stat-item__label">{labels.templatesScanned}</span>
          </div>
        </div>
      </section>

      {/* -- Warnings ------------------------------------------------------- */}
      {payload.warnings.length > 0 && (
        <section className="product-section">
          <WarningPanel
            warnings={payload.warnings}
            label="Statement warnings"
          />
        </section>
      )}

      {/* -- HTML preview --------------------------------------------------- */}
      <section className="product-section" aria-labelledby="stmt-html-h">
        <h2 id="stmt-html-h" className="section-title">
          {labels.yourStatement}
        </h2>
        <p className="text-muted text-sm mb-2">{labels.downloadNotice}</p>
        <p className="text-muted text-sm mb-2">{labels.legalDisclaimer}</p>
        <div
          className="statement-preview"
          aria-label="Statement HTML preview"
          // Safe: HTML is sanitized with DOMPurify before rendering
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(payload.statementHtml, {
              ALLOWED_TAGS: [
                "p",
                "h1",
                "h2",
                "h3",
                "strong",
                "em",
                "a",
                "ul",
                "ol",
                "li",
                "table",
                "thead",
                "tbody",
                "tr",
                "th",
                "td",
                "br",
                "hr",
              ],
              ALLOWED_ATTR: ["href", "target", "rel", "title"],
            }),
          }}
        />
        <CopyHtmlButton html={payload.statementHtml} labels={labels} />
      </section>

      {/* -- Accessibility badge -------------------------------------------- */}
      <BadgeSection websiteId={websiteId!} labels={labels} />

      {/* -- Interactive widget --------------------------------------------- */}
      <WidgetEmbedSection websiteId={websiteId!} labels={labels} />

      {/* -- Related pages -------------------------------------------------- */}
      <section className="product-section">
        <div className="action-links">
          <Link
            to={`/websites/${websiteId}/report`}
            className="btn btn--secondary"
          >
            {labels.fullReport}
          </Link>
          <Link
            to={`/websites/${websiteId}`}
            className="btn btn--secondary"
          >
            {labels.dashboard}
          </Link>
        </div>
      </section>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Language selector                                                          */
/* -------------------------------------------------------------------------- */

function LanguageSelector({
  lang,
  onLangChange,
}: {
  lang: StatementLanguageCode;
  onLangChange: (code: StatementLanguageCode) => void;
}) {
  const labels = STATEMENT_UI_LABELS[lang];

  return (
    <div className="statement-lang-select">
      <label htmlFor="stmt-lang">{labels.languageSelectorLabel}</label>
      <select
        id="stmt-lang"
        value={lang}
        onChange={(e) =>
          onLangChange(e.target.value as StatementLanguageCode)
        }
      >
        {STATEMENT_LANGUAGES.map((l) => (
          <option key={l.code} value={l.code}>
            {l.nativeLabel} ({l.label})
          </option>
        ))}
      </select>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Copy HTML button                                                           */
/* -------------------------------------------------------------------------- */

const APP_ORIGIN =
  typeof import.meta.env.VITE_APP_ORIGIN === "string" &&
  import.meta.env.VITE_APP_ORIGIN
    ? import.meta.env.VITE_APP_ORIGIN
    : "https://accessimonitor.com";

type UiLabels = (typeof STATEMENT_UI_LABELS)[StatementLanguageCode];

function CopyHtmlButton({
  html,
  labels,
}: {
  html: string;
  labels: UiLabels;
}) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    navigator.clipboard
      .writeText(html)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        // Clipboard not available -- ignored
      });
  }

  return (
    <button
      type="button"
      className="btn btn--secondary btn--sm mt-2"
      onClick={handleCopy}
    >
      {copied ? labels.copied : labels.copyHtml}
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/*  Badge section                                                              */
/* -------------------------------------------------------------------------- */

function BadgeSection({
  websiteId,
  labels,
}: {
  websiteId: string;
  labels: UiLabels;
}) {
  const [copied, setCopied] = useState(false);

  const badgeSrc = `/api/public/badge/${websiteId}`;
  const embedCode = `<a href="${APP_ORIGIN}/statement/${websiteId}">\n  <img src="${APP_ORIGIN}/api/public/badge/${websiteId}" alt="Automated accessibility score" />\n</a>`;

  function handleCopy() {
    navigator.clipboard
      .writeText(embedCode)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        // Fallback: select text in textarea
      });
  }

  return (
    <section className="product-section" aria-labelledby="stmt-badge-h">
      <h2 id="stmt-badge-h" className="section-title">
        {labels.accessibilityBadge}
      </h2>
      <p className="text-muted text-sm mb-2">
        Add this badge to your website to show your latest automated
        accessibility score. The badge links to your public accessibility
        statement. It updates automatically when a new scan completes.
      </p>
      <p className="text-muted text-sm mb-2">
        The badge reflects automated scan results and does not indicate full
        WCAG conformance.
      </p>

      <div className="mb-3">
        <img
          src={badgeSrc}
          alt="Accessibility score badge preview"
          className="d-block"
        />
      </div>

      <p className="text-sm mb-1">Embed code:</p>
      <pre className="code-block code-block--light">
        {embedCode}
      </pre>

      <button
        type="button"
        className="btn btn--secondary btn--sm mt-2"
        onClick={handleCopy}
      >
        {copied ? labels.copied : "Copy embed code"}
      </button>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Widget embed section                                                       */
/* -------------------------------------------------------------------------- */

function WidgetEmbedSection({
  websiteId,
  labels,
}: {
  websiteId: string;
  labels: UiLabels;
}) {
  const [copied, setCopied] = useState(false);

  const widgetCode = `<script src="${APP_ORIGIN}/api/public/widget.js" data-website-id="${websiteId}" async></script>`;

  function handleCopy() {
    navigator.clipboard
      .writeText(widgetCode)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        // Clipboard not available -- ignored
      });
  }

  return (
    <section className="product-section" aria-labelledby="stmt-widget-h">
      <h2 id="stmt-widget-h" className="section-title">
        {labels.interactiveWidget}
      </h2>
      <p className="text-muted text-sm mb-2">
        Add an interactive accessibility widget to your website. It displays
        your automated scan score and links to your accessibility statement.
        Self-contained, loads asynchronously, lightweight.
      </p>
      <p className="text-muted text-sm mb-2">
        The widget displays your automated scan score and links to your
        accessibility statement. It does not indicate WCAG conformance.
      </p>

      <p className="text-sm mb-1">Embed code:</p>
      <pre className="code-block code-block--light">
        {widgetCode}
      </pre>

      <button
        type="button"
        className="btn btn--secondary btn--sm mt-2"
        onClick={handleCopy}
      >
        {copied ? labels.copied : "Copy widget code"}
      </button>
    </section>
  );
}
